import { GRAHA_HI_EN_BY_EN } from './jyotishaGrahaNames';
import { houseTopicHi } from './jyotishaHouseOrdinalHi';

const HOUSE_ORDINAL_EN = [
  '1st House',
  '2nd House',
  '3rd House',
  '4th House',
  '5th House',
  '6th House',
  '7th House',
  '8th House',
  '9th House',
  '10th House',
  '11th House',
  '12th House',
];

/** Planet title lines from the user's placement phala list. */
export const DRISHTI_PLACEMENT_PHALA_META = {
  Sun: { emoji: '☀️', themeEn: 'Soul & Authority', themeHi: 'आत्मा और अधिकार' },
  Moon: { emoji: '🌙', themeEn: 'Mind & Emotions', themeHi: 'मन और भावनाएँ' },
  Mars: { emoji: '🔥', themeEn: 'Action & Energy', themeHi: 'कर्म और ऊर्जा' },
  Mercury: { emoji: '☿️', themeEn: 'Intellect & Speech', themeHi: 'बुद्धि और वाणी' },
  Jupiter: { emoji: '♃', themeEn: 'Wisdom & Abundance', themeHi: 'ज्ञान और समृद्धि' },
  Venus: { emoji: '✨', themeEn: 'Love & Luxury', themeHi: 'प्रेम और वैभव' },
  Saturn: { emoji: '♄', themeEn: 'Delay & Discipline', themeHi: 'विलंब और अनुशासन' },
  Rahu: { emoji: '☊', themeEn: 'Obsession & Ambition', themeHi: 'लगन और महत्वाकांक्षा' },
  Ketu: { emoji: '☋', themeEn: 'Detachment & Liberation', themeHi: 'वैराग्य और मोक्ष' },
};

function houseOrdinalEn(house) {
  return HOUSE_ORDINAL_EN[house - 1] ?? `${house}th House`;
}

function planetTitleEn(grahaEn) {
  const meta = DRISHTI_PLACEMENT_PHALA_META[grahaEn];
  const labelEn = GRAHA_HI_EN_BY_EN[grahaEn]?.en ?? grahaEn;
  if (!meta) return labelEn;
  return `${meta.emoji} The ${labelEn} (${meta.themeEn})`;
}

function planetTitleHi(grahaEn) {
  const meta = DRISHTI_PLACEMENT_PHALA_META[grahaEn];
  const labelHi = GRAHA_HI_EN_BY_EN[grahaEn]?.hi ?? grahaEn;
  if (!meta) return labelHi;
  return `${meta.emoji} ${labelHi} (${meta.themeHi})`;
}

/**
 * Body text only (no graha/house prefix) - for phaladesh sections that add their own labels.
 * @param {string} grahaEn
 * @param {number} house
 * @returns {{ hi: string; en: string } | null}
 */
export function drishtiPlacementPhalaBody(grahaEn, house) {
  const textEn = DRISHTI_PLACEMENT_PHALA_EN[grahaEn]?.[house];
  const textHi = DRISHTI_PLACEMENT_PHALA_HI[grahaEn]?.[house];
  if (!textEn && !textHi) return null;
  return {
    hi: textHi ?? textEn,
    en: textEn ?? textHi,
  };
}

/**
 * Full one-line hint matching the user's list format.
 * @param {string} grahaEn
 * @param {number} house
 * @returns {{ hi: string; en: string } | null}
 */
export function drishtiPlacementPhalaLine(grahaEn, house) {
  const body = drishtiPlacementPhalaBody(grahaEn, house);
  if (!body) return null;
  return {
    hi: `${houseTopicHi(house)}: ${planetTitleHi(grahaEn)} ${body.hi}`,
    en: `${houseOrdinalEn(house)}: ${planetTitleEn(grahaEn)} ${body.en}`,
  };
}

/** @type {Record<string, Record<number, string>>} */
export const DRISHTI_PLACEMENT_PHALA_EN = {
  Sun: {
    1: 'A powerful, unshakeable sense of self-identity and a natural urge to lead.',
    2: 'Wealth and self-worth are deeply tied to family pride and parental legacy.',
    3: 'Great courage, willpower, and a highly impactful, authoritative way of communicating.',
    4: 'A deep emotional need to be the central authority or ruler within the home.',
    5: 'Massive creative intelligence, a proud ego, and deep love for children or arts.',
    6: 'Victory over enemies, legal disputes, and a highly disciplined approach to daily work.',
    7: 'A dominant, high-status partner who brings power conflicts into the marriage.',
    8: 'Sudden, life-altering transformations regarding your personal power, soul, and hidden secrets.',
    9: 'A lifelong devotion to higher truth, philosophy, and a powerful relationship with the father.',
    10: 'Highest peak of career success, public fame, authority, and professional recognition.',
    11: 'Powerful, influential network circles and the fulfillment of major lifelong desires.',
    12: 'Isolation, deep spiritual awakening, and a tendency to spend wealth on charitable causes.',
  },
  Moon: {
    1: 'A highly sensitive, deeply emotional personality with a constantly fluctuating mood.',
    2: 'Financial income and emotional peace fluctuate constantly based on your state of mind.',
    3: 'A restless mind that thrives on constant travel, writing, and communication.',
    4: 'A profound, unshakeable emotional attachment to the mother and the homeland.',
    5: 'A deeply romantic heart with intense emotional attachment to children and creative ideas.',
    6: 'Emotional anxiety caused by daily routine work, debts, or minor health issues.',
    7: 'An intense emotional dependency on the partner for mental peace and balance.',
    8: 'Deep psychic intuition paired with intense, hidden psychological ups and downs.',
    9: 'Emotional fulfillment found through long journeys, spiritual pilgrimages, and higher wisdom.',
    10: 'A public career that constantly fluctuates in status and relies on public approval.',
    11: 'A massive, ever-changing circle of friends who feel like emotional family.',
    12: 'Intense vivid dreams, a love for solitude, and deep emotional escapism.',
  },
  Mars: {
    1: 'An aggressive, highly energetic personality with physical scars or a warrior mindset.',
    2: 'Harsh, aggressive speech patterns and a highly competitive drive to earn money.',
    3: 'Intense courage, physical strength, and frequent conflicts or arguments with siblings.',
    4: 'Domestic arguments, property disputes, and an underlying tension within the home environment.',
    5: 'A highly competitive nature in sports, speculative business, and intense romantic passions.',
    6: 'An unstoppable drive to defeat rivals, overcome diseases, and handle heavy work pressure.',
    7: 'Intense passion mixed with frequent, fiery arguments and conflicts in marriage (Manglik Dosha).',
    8: 'Sudden accidents, surgeries, or intense arguments over joint assets and inheritance.',
    9: 'Fiery arguments over religious beliefs and a highly independent, rebellious worldview.',
    10: 'High leadership, absolute dominance in the workplace, and a competitive career drive.',
    11: 'Aggressive networking to achieve goals and fighting for social causes.',
    12: 'Suppressed anger, secret enemies, and restless sleep due to overactive mental energy.',
  },
  Mercury: {
    1: 'A youthful appearance, witty sense of humor, and an incredibly fast-thinking brain.',
    2: 'A highly analytical mind for finances and a career based entirely on speech or writing.',
    3: 'Exceptional writing skills, frequent short travels, and a hyperactive communication style.',
    4: 'A highly intellectual home environment filled with books, debates, and learning.',
    5: 'Brilliant analytical skills, a love for puzzles, and a talent for stock trading.',
    6: 'A hyper-analytical focus on daily routines, medicine, litigation, or critical problem-solving.',
    7: 'A youthful, highly communicative, and intellectually stimulating marriage partner.',
    8: 'Deep research skills, secret investigative talents, and hidden occult knowledge.',
    9: 'Higher education in publishing, law, or mass communication, and a logical approach to religion.',
    10: 'A career completely dominated by business, consultancy, media, or data analytics.',
    11: 'A massive network of intellectual friends, businessmen, and counselors.',
    12: 'An overthinking mind that thrives in creative writing, imagination, and spiritual isolation.',
  },
  Jupiter: {
    1: 'A highly optimistic, magnetic personality blessed with divine luck and wisdom.',
    2: 'Immense wealth accumulation, a large respected family, and highly ethical, sweet speech.',
    3: 'Great wisdom in writing, counseling, and highly supportive relationships with siblings.',
    4: 'A massive, peaceful home environment and profound emotional happiness from the mother.',
    5: 'Outstanding creative intelligence, massive luck in investments, and highly virtuous, successful children.',
    6: 'Divine protection from enemies, diseases, and a natural talent for healing others.',
    7: 'A highly wise, spiritual, and deeply supportive life partner who brings immense good luck.',
    8: 'Massive financial inheritance, deep occult secrets, and a long, spiritually protected life.',
    9: 'Extreme good fortune, high spiritual wisdom, and deep guidance from gurus and fathers.',
    10: 'High professional respect, a highly moral career path, and immense workplace honor.',
    11: 'Massive financial gains from an elite, wise, and highly supportive social circle.',
    12: 'Exceptional spiritual liberation (Moksha) tendencies and heavy spending on divine charity.',
  },
  Venus: {
    1: 'An incredibly attractive, charming physical appearance and a deeply artistic soul.',
    2: 'A luxurious lifestyle, immense wealth, beautiful facial features, and a sweet voice.',
    3: 'Artistic hands (painting, music), pleasant communication, and peaceful travels.',
    4: 'A beautiful, luxurious home filled with comfort, vehicles, arts, and happiness.',
    5: 'A highly creative mind, intense romantic luck, and artistic children.',
    6: 'Disputes regarding relationships, luxury expenses, or health issues related to kidneys/sugar.',
    7: 'A deeply loving, physically attractive, and harmonious marriage partner.',
    8: 'Sudden financial gains through marriage or dark, intense hidden romantic passions.',
    9: 'A love for foreign cultures, beautiful long travels, and philosophical romance.',
    10: 'A glamourous career in arts, media, fashion, luxury items, or public relations.',
    11: 'A major network of wealthy female friends and constant gains from creative arts.',
    12: 'High bed comforts, luxury vacations, and spending money on foreign pleasures.',
  },
  Saturn: {
    1: 'A serious, mature personality burdened with heavy responsibilities from early childhood.',
    2: 'Strict financial restriction in early life, forcing you to learn hard money management.',
    3: 'Extreme delay in success until age 32, but builds unshakeable, disciplined willpower.',
    4: 'A strict, emotionally cold home environment with heavy domestic duties.',
    5: 'Delayed childbirth, serious romantic relationships, and a deeply disciplined creative focus.',
    6: 'Massive capacity for relentless hard work, crushing debt, and defeating chronic diseases.',
    7: 'A delayed marriage (often after age 28 or 36) to a very mature, practical partner.',
    8: 'A very long life span but accompanied by sudden, heavy delays in receiving inheritance.',
    9: 'Late luck in life, a highly orthodox or strict rigid view of religion and higher law.',
    10: 'Slow, agonizingly painful career growth that eventually leads to permanent, massive power.',
    11: 'A very small, limited group of older friends and slow but steady long-term financial gains.',
    12: 'Chronic loneliness, insomnia, or a highly disciplined, isolated spiritual life.',
  },
  Rahu: {
    1: 'An obsessive desire for self-fame, a unique persona, and unconventional behavior.',
    2: 'Extreme obsession with hoarding wealth, sudden foreign income, and erratic speech habits.',
    3: 'Unstoppable courage, massive success in digital media, and unconventional risks.',
    4: 'Deep dissatisfaction with the homeland, a desire for foreign homes, and domestic unrest.',
    5: 'Speculative stock market gambles, unusual romance, and unconventional children.',
    6: 'Complete annihilation of enemies using highly unconventional, clever tactics.',
    7: 'An unconventional, foreign, or inter-caste marriage based on sudden obsession.',
    8: 'Sudden, shocking transformations, extreme occult obsession, and secret unearned wealth.',
    9: 'Rebellion against traditional religion and a strong desire for foreign philosophies.',
    10: 'A meteoric, sudden rise to power using foreign connections or manipulative career tactics.',
    11: 'Massive financial windfalls from foreign networks, technology, or large corporations.',
    12: 'Heavy expenditure on secret habits, foreign trips, and intense subconscious illusion.',
  },
  Ketu: {
    1: 'Deep identity crisis, complete lack of self-ego, and an intensely spiritual nature.',
    2: 'Complete financial detachment, unexpected losses, and blunt speech.',
    3: 'High internal courage but absolute detachment from siblings and casual gossip.',
    4: 'Feeling like a complete stranger in your own home and detachment from the mother.',
    5: 'Disinterest in material education or speculation, matched with deep past-life intuitive knowledge.',
    6: 'Complete fearlessness against enemies and an indifferent attitude toward daily struggles.',
    7: 'Feeling emotionally disconnected from the spouse, leading to a spiritualized marriage.',
    8: 'Absolute mastery over occult sciences, deep meditation, and sudden spiritual awakenings.',
    9: 'Complete disregard for dogmatic rituals and a direct, mystical connection to the divine.',
    10: 'Deep dissatisfaction with corporate fame, leading to a desire to quit the rat race.',
    11: 'Complete indifference toward social popularity and keeping zero fake friends.',
    12: 'Absolute isolation, profound spiritual enlightenment, and final liberation (Moksha).',
  },
};

/** @type {Record<string, Record<number, string>>} */
export const DRISHTI_PLACEMENT_PHALA_HI = {
  Sun: {
    1: 'अटूट आत्म-पहचान और स्वाभाविक नेतृत्व की प्रवृत्ति।',
    2: 'धन और आत्म-मूल्य परिवार की गरिमा और पितृ-धन से गहराई से जुड़े।',
    3: 'महान साहस, इच्छाशक्ति और प्रभावशाली, अधिकारिक संवाद शैली।',
    4: 'घर में केंद्रीय अधिकार या शासक बनने की गहरी भावनात्मक आवश्यकता।',
    5: 'विशाल रचनात्मक बुद्धि, गर्वित अहंकार और संतान या कला के प्रति गहरा प्रेम।',
    6: 'शत्रुओं पर विजय, कानूनी विवाद और दैनिक कार्य में अत्यंत अनुशासन।',
    7: 'प्रभावी, उच्च दर्जे का जीवनसाथी जो विवाह में सत्ता-संघर्ष लाता है।',
    8: 'व्यक्तिगत शक्ति, आत्मा और गुप्त रहस्यों में अचानक, जीवन बदलने वाले परिवर्तन।',
    9: 'उच्च सत्य, दर्शन और पिता से गहरे संबंध के प्रति आजीवन समर्पण।',
    10: 'करियर की सर्वोच्च ऊँचाई, सार्वजनिक यश, अधिकार और पेशेवर मान्यता।',
    11: 'प्रभावशाली सामाजिक मंडल और प्रमुख जीवन-इच्छाओं की पूर्ति।',
    12: 'एकांत, गहरी आध्यात्मिक जागृति और धन का दान में व्यय।',
  },
  Moon: {
    1: 'अत्यंत संवेदनशील, गहन भावनात्मक व्यक्तित्व और लगातार बदलता मनोदशा।',
    2: 'आय और मानसिक शांति मन की स्थिति के अनुसार निरंतर उतार-चढ़ाव करती है।',
    3: 'बेचैन मन जो यात्रा, लेखन और संवाद में पनपता है।',
    4: 'माता और मातृभूमि से अटूट, गहन भावनात्मक लगाव।',
    5: 'गहरा रоманटिक हृदय, संतान और रचनात्मक विचारों से तीव्र भावनात्मक जुड़ाव।',
    6: 'दैनिक कार्य, ऋण या छोटी स्वास्थ्य समस्याओं से भावनात्मक चिंता।',
    7: 'मानसिक शांति के लिए जीवनसाथी पर तीव्र भावनात्मक निर्भरता।',
    8: 'गहरी अंतर्ज्ञान शक्ति और छिपे मनोवैज्ञानिक उतार-चढ़ाव।',
    9: 'लंबी यात्राओं, तीर्थ और उच्च ज्ञान से भावनात्मक पूर्ति।',
    10: 'सार्वजनिक करियर जिसकी स्थिति बार-बार बदलती है और जन-मान्यता पर निर्भर।',
    11: 'विशाल, बदलता मित्र-मंडल जो भावनात्मक परिवार जैसा लगे।',
    12: 'तीव्र स्वप्न, एकांत प्रेम और गहरी भावनात्मक पलायन प्रवृत्ति।',
  },
  Mars: {
    1: 'आक्रामक, अत्यंत ऊर्जावान व्यक्तित्व, शारीरिक निशान या योद्धा मानसिकता।',
    2: 'कठोर, आक्रामक वाणी और धन कमाने की प्रतिस्पर्धी प्रवृत्ति।',
    3: 'तीव्र साहस, शारीरिक शक्ति और भाई-बहनों से बार-बार विवाद।',
    4: 'घर में झगड़े, संपत्ति विवाद और अंतर्निहित तनाव।',
    5: 'खेल, सट्टा और प्रेम में अत्यंत प्रतिस्पर्धी स्वभाव।',
    6: 'प्रतिद्वंद्वियों को हराने, रोगों पर विजय और भारी कार्य-दबाव सहन करने की अदम्य प्रेरणा।',
    7: 'तीव्र प्रेम के साथ बार-बार झगड़े और विवाह में संघर्ष (मांगलिक दोष)।',
    8: 'अचानक दुर्घटना, शल्यक्रिया या संयुक्त संपत्ति और विरासत पर तीव्र विवाद।',
    9: 'धार्मिक मान्यताओं पर उग्र बहस और स्वतंत्र, विद्रोही दृष्टिकोण।',
    10: 'उच्च नेतृत्व, कार्यस्थल में प्रभुत्व और प्रतिस्पर्धी करियर-प्रेरणा।',
    11: 'लक्ष्य हासिल करने के लिए आक्रामक नेटवर्किंग और सामाजिक कारणों के लिए संघर्ष।',
    12: 'दबा हुआ क्रोध, गुप्त शत्रु और अतिसक्रिय मन से अशांत नींद।',
  },
  Mercury: {
    1: 'युवा दिखावट, चतुर हास्य-विनोद और अविश्वसनीय रूप से तेज दिमाग।',
    2: 'वित्त के लिए विश्लेषणात्मक मन और वाणी या लेखन पर आधारित करियर।',
    3: 'उत्कृष्ट लेखन कौशल, बार-बार छोटी यात्राएँ और अतिसक्रिय संवाद शैली।',
    4: 'पुस्तकों, वाद-विवाद और शिक्षा से भरा बौद्धिक घरेलू वातावरण।',
    5: 'शानदार विश्लेषणात्मक कौशल, पहेलियों का प्रेम और शेयर-व्यापार की प्रतिभा।',
    6: 'दैनिक दिनचर्या, चिकित्सा, मुकदमेबाजी या समस्या-समाधान पर अतिविश्लेषण।',
    7: 'युवा, अत्यंत संवादशील और बौद्धिक रूप से प्रेरक जीवनसाथी।',
    8: 'गहन अनुसंधान कौशल, गुप्त जाँच-पड़ताल और छिपा तांत्रिक ज्ञान।',
    9: 'प्रकाशन, कानून या जन-संचार में उच्च शिक्षा और धर्म का तार्किक दृष्टिकोण।',
    10: 'व्यापार, परामर्श, मीडिया या डेटा-विश्लेषण से पूर्णतः प्रभावित करियर।',
    11: 'बुद्धिजीवी मित्रों, व्यापारियों और परामर्शदाताओं का विशाल नेटवर्क।',
    12: 'अतिचिंतनशील मन जो रचनात्मक लेखन, कल्पना और आध्यात्मिक एकांत में पनपे।',
  },
  Jupiter: {
    1: 'अत्यंत आशावादी, आकर्षक व्यक्तित्व, दैवीय सौभाग्य और ज्ञान से युक्त।',
    2: 'विशाल धन संचय, बड़ा सम्मानित परिवार और नैतिक, मधुर वाणी।',
    3: 'लेखन, परामर्श में महान ज्ञान और भाई-बहनों से अत्यंत सहयोगी संबंध।',
    4: 'विशाल, शांत घर और माता से गहरी भावनात्मक खुशी।',
    5: 'उत्कृष्ट रचनात्मक बुद्धि, निवेश में बड़ा सौभाग्य और सफल, सद्गुणी संतान।',
    6: 'शत्रुओं और रोगों से दैवीय सुरक्षा तथा दूसरों को ठीक करने की प्रतिभा।',
    7: 'अत्यंत बुद्धिमान, आध्यात्मिक और सहयोगी जीवनसाथी जो बड़ा सौभाग्य लाए।',
    8: 'विशाल वित्तीय विरासत, गहरे तांत्रिक रहस्य और लंबा, आध्यात्मिक रूप से सुरक्षित जीवन।',
    9: 'अत्यधिक सौभाग्य, उच्च आध्यात्मिक ज्ञान और गुरु-पिता से गहरा मार्गदर्शन।',
    10: 'उच्च पेशेवर सम्मान, नैतिक करियर-पथ और कार्यस्थल में अपार गौरव।',
    11: 'विशिष्ट, बुद्धिमान सामाजिक मंडल से विशाल वित्तीय लाभ।',
    12: 'असाधारण मोक्ष-प्रवृत्ति और दैवीय दान में भारी व्यय।',
  },
  Venus: {
    1: 'अत्यंत आकर्षक, मोहक शारीरिक रूप और गहरा कलात्मक स्वभाव।',
    2: 'वैभवपूर्ण जीवनशैली, विशाल धन, सुंदर चेहरे और मधुर स्वर।',
    3: 'कलात्मक हाथ (चित्र, संगीत), सुखद संवाद और शांत यात्राएँ।',
    4: 'सुख-सुविधा, वाहन, कला और खुशी से भरा सुंदर, वैभवी घर।',
    5: 'अत्यंत रचनात्मक मन, तीव्र प्रेम-सौभाग्य और कलात्मक संतान।',
    6: 'संबंधों, विलासिता-खर्च या गुर्दे/मधुमेह से संबंधित स्वास्थ्य विवाद।',
    7: 'गहरा प्रेम, शारीरिक आकर्षण और सामंजस्यपूर्ण जीवनसाथी।',
    8: 'विवाह से अचानक वित्तीय लाभ या गहरे, छिपे प्रेम-ज्वार।',
    9: 'विदेशी संस्कृति, सुंदर लंबी यात्राएँ और दार्शनिक प्रेम का प्रेम।',
    10: 'कला, मीडिया, फैशन, विलासिता या जन-संपर्क में चमकदार करियर।',
    11: 'धनी महिला मित्रों का बड़ा नेटवर्क और रचनात्मक कलाओं से निरंतर लाभ।',
    12: 'उच्च शयन-सुख, विलासी अवकाश और विदेशी सुखों पर धन व्यय।',
  },
  Saturn: {
    1: 'गंभीर, परिपक्व व्यक्तित्व, बचपन से ही भारी जिम्मेदारियों से बोझिल।',
    2: 'प्रारंभिक जीवन में कठोर वित्तीय प्रतिबंध, कठिन धन-प्रबंधन सीखना पड़ता है।',
    3: '32 वर्ष की आयु तक सफलता में अत्यधिक विलंब, पर अटूट अनुशासित इच्छाशक्ति।',
    4: 'कठोर, भावनात्मक रूप से शीत घर और भारी घरेलू कर्तव्य।',
    5: 'संतान में विलंब, गंभीर प्रेम-संबंध और गहरा अनुशासित रचनात्मक ध्यान।',
    6: 'अथक परिश्रम की विशाल क्षमता, भारी ऋण और पुरानी बीमारियों पर विजय।',
    7: 'विलंबित विवाह (अक्सर 28 या 36 वर्ष के बाद) परिपक्व, व्यावहारिक जीवनसाथी से।',
    8: 'बहुत लंबा जीवनकाल, पर विरासत प्राप्ति में अचानक, भारी विलंब।',
    9: 'जीवन में देर से सौभाग्य, धर्म और उच्च नियम के प्रति कठोर, रूढ़िवादी दृष्टि।',
    10: 'धीमा, कष्टदायक करियर-विकास जो अंततः स्थायी, विशाल शक्ति देता है।',
    11: 'बहुत छोटा, सीमित, वृद्ध मित्र-समूह और धीमे पर स्थिर दीर्घकालिक लाभ।',
    12: 'दीर्घकालिक एकांत, अनिद्रा या अत्यंत अनुशासित, एकांत आध्यात्मिक जीवन।',
  },
  Rahu: {
    1: 'आत्म-प्रसिद्धि की अत्यधिक लगन, अनोखा व्यक्तित्व और अपरंपरागत व्यवहार।',
    2: 'धन संचय की चरम लगन, अचानक विदेशी आय और अनियमित वाणी।',
    3: 'अदम्य साहस, डिजिटल मीडिया में बड़ी सफलता और अपरंपरागत जोखिम।',
    4: 'मातृभूमि से गहरी असंतोष, विदेशी घर की इच्छा और घरेलू अशांति।',
    5: 'सट्टा-शेयर जुआ, असामान्य प्रेम और अपरंपरागत संतान।',
    6: 'अपरंपरागत, चतुर तरीकों से शत्रुओं का पूर्ण विनाश।',
    7: 'अचानक लगन पर आधारित अपरंपरागत, विदेशी या अंत-जातीय विवाह।',
    8: 'अचानक, चौंकाने वाले परिवर्तन, चरम तांत्रिक लगन और गुप्त अवैध धन।',
    9: 'पारंपरिक धर्म के विरुद्ध विद्रोह और विदेशी दर्शन की तीव्र इच्छा।',
    10: 'विदेशी संपर्क या चालाक करियर-तरीकों से अचानक, तेज़ सत्ता-उदय।',
    11: 'विदेशी नेटवर्क, तकनीक या बड़े निगमों से विशाल, अचानक वित्तीय लाभ।',
    12: 'गुप्त आदतों, विदेशी यात्राओं पर भारी खर्च और तीव्र अवचेतन भ्रम।',
  },
  Ketu: {
    1: 'गहरी पहचान-संकट, पूर्ण अहंकार-रहितता और अत्यंत आध्यात्मिक स्वभाव।',
    2: 'पूर्ण वित्तीय वैराग्य, अप्रत्याशित हानि और सीधी, कठोर वाणी।',
    3: 'अंतः साहस पर भाई-बहनों और हल्की बातचीत से पूर्ण वैराग्य।',
    4: 'अपने घर में अजनबी जैसा महसूस और माता से वैराग्य।',
    5: 'भौतिक शिक्षा या सट्टे में अरुचि, साथ ही गहरा पूर्वजन्म-ज्ञान।',
    6: 'शत्रुओं के प्रति पूर्ण निर्भयता और दैनिक संघर्षों के प्रति उदासीनता।',
    7: 'जीवनसाथी से भावनात्मक दूरी, आध्यात्मिक विवाह की ओर।',
    8: 'तांत्रिक विज्ञान में पूर्ण निपुणता, गहन ध्यान और अचानक आध्यात्मिक जागृति।',
    9: 'कठोर अनुष्ठानों की उपेक्षा और ईश्वर से प्रत्यक्ष, रहस्यमय संबंध।',
    10: 'कॉर्पोरेट यश से गहरी असंतोष, दौड़ छोड़ने की इच्छा।',
    11: 'सामाजिक लोकप्रियता के प्रति पूर्ण उदासीनता, नकली मित्र शून्य।',
    12: 'पूर्ण एकांत, गहन आध्यात्मिक ज्ञान और अंतिम मोक्ष।',
  },
};
